import React from 'react'
import PropTypes from 'prop-types'
import { useForm, FormProvider } from 'react-hook-form'
import styled from '@emotion/styled'

import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'

import { theme } from '../../themes/theme'
import { config } from '../../config'
import { formatFOIDetails } from '../../util/foiFormatting'
import { getFoiStepperArray } from '../../util/foiConsts'
import { Form, DarkBGPaddedFormDiv, FlexEndButtonErrorContainer, SpaceBetweenRowContainer, FlexEndContainer, FlexColumn } from '../styles/Containers'
import { WarningMessage, ErrorMessage } from '../common/UserMessage'
import { Checkbox, FormControlLabel } from '../styles/Checkbox'
import Button, { SecondaryButton } from '../common/Button'
import BodyText, { ErrorText } from '../styles/Text'
import Carousel from '../common/Carousel'
import FOIRequest from './FOIRequest'

const NavButtonContainer = styled(FlexEndContainer)`
  text-wrap: nowrap;
  @media only screen and (max-width: 600px) {
    width: 100%;
  }
`

const Label = styled(FormControlLabel)`
  margin: -8px 0;
`

const Red = styled.span`
  color: ${theme.colors.error};
`

function ReviewStep({ request, onBack = () => {}, onComplete = () => {}, error = false, loading = false }) {
  const methods = useForm({ shouldUseNativeValidation: false })
  const { handleSubmit, register, formState } = methods

  const onError = (errors, e) => console.error('Error', errors, e)
  const onSubmit = data => onComplete(data)

  const getLabel = () => (
    <>
      I confirm I am {request.requestor.firstName} {request.requestor.lastName} and that the information in this request is complete and accurate.
      <Red> *</Red>
    </>
  )

  const confirmationError = !!formState.errors.confirmation
  const confirmationErrorHelperText = formState.errors?.confirmation?.message

  const contactText =
    config.foi.privacyTeamNumber !== '' ? `${config.foi.privacyTeamEmail} or ${config.foi.privacyTeamNumber}` : config.foi.privacyTeamEmail

  return (
    <FlexColumn>
      <FormProvider {...methods}>
        <Form onSubmit={handleSubmit(onSubmit, onError)} gap="30px">
          <SpaceBetweenRowContainer gap="50px">
            <NavButtonContainer>
              <SecondaryButton padding="large" onClick={onBack} disabled={loading}>
                <ArrowBackIosIcon />
                Edit Request
              </SecondaryButton>
            </NavButtonContainer>
            <Carousel steps={getFoiStepperArray(config.foi.APPLICATION_FEE)} activeStep={2} />
          </SpaceBetweenRowContainer>
          <DarkBGPaddedFormDiv>
            <FOIRequest foiRequest={formatFOIDetails(request)} />
            <WarningMessage>
              {'Clicking “Submit Request” will send this request to the City and cannot be undone. '}
              {'Once submitted, to cancel the request please contact us at '}
              {contactText}.
            </WarningMessage>
            <FlexEndButtonErrorContainer gap="0">
              <Label {...register('confirmation', { required: 'You must confirm before submitting.' })} control={<Checkbox />} label={getLabel()} />
              {confirmationError && <ErrorText role="alert">{confirmationErrorHelperText}</ErrorText>}
            </FlexEndButtonErrorContainer>
            <FlexEndButtonErrorContainer>
              <Button padding="large" type="submit" loading={loading}>
                Submit Request
                <ArrowForwardIosIcon />
              </Button>
              {error && (
                <ErrorMessage>{typeof error === 'string' ? error : 'An error occurred while submitting the FOI. Please try again.'}</ErrorMessage>
              )}
            </FlexEndButtonErrorContainer>
            <BodyText>{config.foi.request.disclaimer}</BodyText>
          </DarkBGPaddedFormDiv>
        </Form>
      </FormProvider>
    </FlexColumn>
  )
}

ReviewStep.propTypes = {
  request: PropTypes.object.isRequired,
  onBack: PropTypes.func,
  onComplete: PropTypes.func,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  loading: PropTypes.bool
}

export default ReviewStep
